<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  defineAsyncComponent,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import internalChatCable from 'dashboard/helper/internalChatCable';
import ChatList from './ChatList.vue';
import EmptyState from './EmptyState.vue';
import CallWidget from './CallWidget.vue';

const ChatView = defineAsyncComponent(() => import('./ChatView.vue'));

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const activeChat = computed(() => store.getters['internalChats/getActiveChat']);
const currentUser = computed(() => store.getters.getCurrentUser);

const selectChatById = async chatId => {
  if (!chatId) {
    store.dispatch('internalChats/setActiveChat', null);
    return;
  }
  const chats = store.getters['internalChats/getChats'];
  const target = chats.find(c => String(c.id) === String(chatId));
  if (!target) return;
  store.dispatch('internalChats/setActiveChat', target);
  await store.dispatch('internalChats/get', target.id);
  await store.dispatch('internalChats/markRead', target.id);
  internalChatCable.subscribeToChat(target.id);
};

const setActive = chat => {
  if (!chat) return;
  if (String(route.params.chatId) === String(chat.id)) {
    selectChatById(chat.id);
    return;
  }
  router.push(accountScopedRoute('internal_chat_detail', { chatId: chat.id }));
};

onMounted(async () => {
  await store.dispatch('internalChats/list');

  // Ensure the agents store is populated so DM presence dots show on cold-start
  // (agents/get self-guards against refetch — no cost if already loaded).
  store.dispatch('agents/get');

  const token = currentUser.value?.pubsub_token;
  if (token) {
    internalChatCable.init(store, token);
  }

  if (route.params.chatId) {
    selectChatById(route.params.chatId);
  }
});

watch(
  () => route.params.chatId,
  newId => selectChatById(newId)
);

onBeforeUnmount(() => {
  // Only unsubscribe the per-chat channel here; the account-level unread feed
  // intentionally stays alive so the sidebar badge keeps updating off-screen.
  // Full teardown (account feed + consumer) happens on logout — already wired in
  // auth store logout() -> internalChatCable.disconnect() (IC-11 verified).
  internalChatCable.unsubscribeFromChat();
});
</script>

<template>
  <div class="flex h-full w-full overflow-hidden bg-n-background">
    <!-- Left sidebar: chat list (fixed 320px) -->
    <div class="w-[320px] flex-shrink-0 h-full overflow-hidden">
      <ChatList @select-chat="setActive" />
    </div>

    <!-- Right panel: active chat or empty state -->
    <div class="flex-1 h-full overflow-hidden">
      <ChatView v-if="activeChat" :chat="activeChat" />
      <EmptyState v-else />
    </div>

    <!-- Persistent call widget (incoming/outgoing/active) — survives chat switches -->
    <CallWidget />
  </div>
</template>
